import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_QAS, URL_QAS } from "@/constants/api_endpoints";
import {
	IProcessQuestionsProps,
	IQasProcessingQuestionsState,
	IResponseLogProcessQuestions,
	IResponseProcessQuestionsStatus,
} from "@/types/qas/qasProcessingQuestionsTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: IQasProcessingQuestionsState = {
	processingQuestionsStart: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	processingQuestionsStop: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	processingQuestionsStatus: {
		requestStatus: RequestStatus.IDLE,
		error: "",
		responseStatus: "stopped",
		progress: 0,
	},
	processingQuestionsLog: {
		status: RequestStatus.IDLE,
		data: [],
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// массовая обработка вопросов
export const processQuestions = createAsyncThunk(
	"qasProcessingQuestions/start",
	async ({ formData, categoryId, channel, caching, gpt, addAlias, threshold, modelGpt, format, promptTemplateId }: IProcessQuestionsProps, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponse> = await axiosApi.post(
				`${URL_QAS}/${ENDPOINTS_QAS.PROCESSING_QUESTIONS_START}`,
				{
					questions: formData.get("file"),
					format,
					category: categoryId,
					channel,
					useCache: caching,
					useGPT: gpt,
					addAlias,
					threshold,
					model: modelGpt,
					template: promptTemplateId,
				},
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	}
);

// остановка массовой обработки вопросов
export const stopProcessingQuestions = createAsyncThunk("qasProcessingQuestions/stop", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.PROCESSING_QUESTIONS_STOP}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// статус массовой обработки вопросов
export const getStatusProcessingQuestions = createAsyncThunk(
	"qasProcessingQuestions/status",
	async (_, { rejectWithValue }) => {
		try {
			const response: AxiosResponse<IResponseProcessQuestionsStatus> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.PROCESSING_QUESTIONS_STATUS}`);
			return response.data;
		} catch (error) {
			const handledError = await handleErrorRTKAndAxiosAsync(error);
			return rejectWithValue(handledError);
		}
	},
	{
		condition: (_, { getState }) => {
			if ((getState() as RootState).qasProcessingQuestions.processingQuestionsStatus.requestStatus === RequestStatus.LOADING) return false;
		},
	}
);

// логи массовой обработки вопросов
export const getLogProcessingQuestions = createAsyncThunk("qasProcessingQuestions/log", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponseLogProcessQuestions | IResponse> = await axiosApi.get(`${URL_QAS}/${ENDPOINTS_QAS.PROCESSING_QUESTIONS_LOG}`);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const qasProcessingQuestionsSlice = createSlice({
	name: "qasProcessingQuestions",
	initialState,
	reducers: {
		// очистка state
		clearState: (state) => {
			state.processingQuestionsStart = initialState.processingQuestionsStart;
			state.processingQuestionsStop = initialState.processingQuestionsStop;
			state.processingQuestionsStatus = initialState.processingQuestionsStatus;
			state.processingQuestionsLog = initialState.processingQuestionsLog;
		},
		// очистка статуса старта массовой обработки вопросов
		clearProcessingQuestionsStart: (state) => {
			state.processingQuestionsStart = initialState.processingQuestionsStart;
		},
		// очистка статуса остановки массовой обработки вопросов
		clearProcessingQuestionsStop: (state) => {
			state.processingQuestionsStop = initialState.processingQuestionsStop;
		},
		// очистка статуса процесса массовой обработки вопросов
		clearProcessingQuestionsStatus: (state) => {
			state.processingQuestionsStatus = initialState.processingQuestionsStatus;
		},
		// очистка логов массовой обработки вопросов
		clearProcessingQuestionsLog: (state) => {
			state.processingQuestionsLog = initialState.processingQuestionsLog;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(processQuestions.pending, (state) => {
				state.processingQuestionsStart.status = RequestStatus.LOADING;
			})
			.addCase(processQuestions.fulfilled, (state, action) => {
				if (action.payload) {
					state.processingQuestionsStart.status = RequestStatus.IDLE;
					state.processingQuestionsStart.error = action.payload.error;
					state.processingQuestionsStart.message = action.payload.message;
				} else state.processingQuestionsStart.status = RequestStatus.FAILED;
			})
			.addCase(processQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.processingQuestionsStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.processingQuestionsStart.error = error;
				state.processingQuestionsStart.message = message;
			})
			.addCase(stopProcessingQuestions.pending, (state) => {
				state.processingQuestionsStop.status = RequestStatus.LOADING;
			})
			.addCase(stopProcessingQuestions.fulfilled, (state, action) => {
				if (action.payload) {
					state.processingQuestionsStop.status = RequestStatus.IDLE;
					state.processingQuestionsStop.error = action.payload.error;
					state.processingQuestionsStop.message = action.payload.message;
				} else state.processingQuestionsStop.status = RequestStatus.FAILED;
			})
			.addCase(stopProcessingQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.processingQuestionsStop.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.processingQuestionsStop.error = error;
				state.processingQuestionsStop.message = message;
			})
			.addCase(getStatusProcessingQuestions.pending, (state) => {
				state.processingQuestionsStatus.requestStatus = RequestStatus.LOADING;
			})
			.addCase(getStatusProcessingQuestions.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.processingQuestionsStatus.requestStatus = RequestStatus.IDLE;
					state.processingQuestionsStatus.error = action.payload.error;
					state.processingQuestionsStatus.responseStatus = action.payload.status;
					state.processingQuestionsStatus.progress = action.payload.progress;
				} else state.processingQuestionsStatus.requestStatus = RequestStatus.FAILED;
			})
			.addCase(getStatusProcessingQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.processingQuestionsStatus.requestStatus = RequestStatus.FAILED;
				const { error } = getResponseError(action);
				state.processingQuestionsStatus.error = error.toString();
			})
			.addCase(getLogProcessingQuestions.pending, (state) => {
				state.processingQuestionsLog.status = RequestStatus.LOADING;
			})
			.addCase(getLogProcessingQuestions.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "answers" in action.payload && Array.isArray(action.payload.answers)) {
					state.processingQuestionsLog.status = RequestStatus.IDLE;
					state.processingQuestionsLog.data = action.payload.answers;
				} else state.processingQuestionsLog.status = RequestStatus.FAILED;
			})
			.addCase(getLogProcessingQuestions.rejected, (state, action: PayloadAction<unknown>) => {
				state.processingQuestionsStart.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.processingQuestionsLog.error = error;
				state.processingQuestionsLog.message = message;
			});
	},
});

export const { clearState, clearProcessingQuestionsStart, clearProcessingQuestionsStop, clearProcessingQuestionsStatus, clearProcessingQuestionsLog } =
	qasProcessingQuestionsSlice.actions;

export const selectProcessingQuestionsStartStatus = (state: RootState) => state.qasProcessingQuestions.processingQuestionsStart;
export const selectProcessingQuestionsStopStatus = (state: RootState) => state.qasProcessingQuestions.processingQuestionsStop;
export const selectProcessingQuestionsProcessStatus = (state: RootState) => state.qasProcessingQuestions.processingQuestionsStatus;
export const selectProcessingQuestionsLog = (state: RootState) => state.qasProcessingQuestions.processingQuestionsLog;

export default qasProcessingQuestionsSlice.reducer;
